import { compareVersions } from '../components/About/helpers';
import releasesApiService, { type ApiGdasStatsEntry, type ApiVdasStatsEntry } from './releasesApiService';

export type SourceStatsBySource = Record<string, Record<string, number>>;

export type SourceStatsFileKey = 'gdas' | 'vdas';

export const GDAS_STATS_COLUMNS = [
  'source',
  'genes',
  'diseases',
  'gdas',
  'Gassocs',
  'Gpapers',
  'Gchemicals',
] as const;

export const VDAS_STATS_COLUMNS = [
  'source',
  'variants',
  'Vdiseases',
  'vdas',
  'Vassocs',
  'Vpapers',
  'Vchemicals',
] as const;

const STATS_FILE_PATHS: Record<SourceStatsFileKey, string> = {
  gdas: '/static/files/gdas_stats.tsv',
  vdas: '/static/files/vdas_stats.tsv',
};

let cachedPromise: Promise<{
  gdas: SourceStatsBySource;
  vdas: SourceStatsBySource;
  gdasRows: ApiGdasStatsEntry[];
  vdasRows: ApiVdasStatsEntry[];
}> | null = null;

function entriesToBySource(entries: Array<ApiGdasStatsEntry | ApiVdasStatsEntry>): SourceStatsBySource {
  const bySource: SourceStatsBySource = {};
  for (const entry of entries) {
    const sourceName = String(entry.source ?? '').trim();
    if (!sourceName) continue;
    bySource[sourceName] = {};
    for (const [key, value] of Object.entries(entry)) {
      if (key === 'source') continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        bySource[sourceName][key] = parsed;
      }
    }
  }
  return bySource;
}

async function loadStatsFromTsv(fileKey: SourceStatsFileKey): Promise<{
  bySource: SourceStatsBySource;
  rows: Array<Record<string, string | number>>;
}> {
  const response = await fetch(STATS_FILE_PATHS[fileKey]);
  const text = await response.text();
  const lines = text.trim().split('\n');
  const headers = lines[0]?.split('\t') ?? [];
  const bySource: SourceStatsBySource = {};
  const rows: Array<Record<string, string | number>> = [];

  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i]?.trim();
    if (!line) continue;
    const values = line.split('\t');
    const sourceName = values[0];
    if (!sourceName) continue;

    const row: Record<string, string | number> = { source: sourceName };
    bySource[sourceName] = {};
    headers.forEach((header, index) => {
      if (header === 'source') return;
      const parsed = Number(values[index]);
      if (!Number.isNaN(parsed)) {
        bySource[sourceName][header] = parsed;
        row[header] = parsed;
      }
    });
    rows.push(row);
  }

  return { bySource, rows };
}

async function loadLatestSourceStatsUncached() {
  let apiGdasRows: ApiGdasStatsEntry[] | null = null;
  let apiVdasRows: ApiVdasStatsEntry[] | null = null;

  try {
    const apiReleasesList = await releasesApiService.getReleasesList();
    const apiVersions = apiReleasesList.map(r => r.version);
    if (apiVersions.length > 0) {
      const latestVersion = [...apiVersions].sort((a, b) => compareVersions(b, a))[0];
      const detail = await releasesApiService.getRelease(latestVersion);

      if (detail.gdasStats?.length) apiGdasRows = detail.gdasStats;
      if (detail.vdasStats?.length) apiVdasRows = detail.vdasStats;
    }
  } catch (e) {
    console.error('Error loading source stats from releases API:', e);
  }

  const needsGdasFallback = !apiGdasRows;
  const needsVdasFallback = !apiVdasRows;

  const [gdasTsv, vdasTsv] = await Promise.all([
    needsGdasFallback ? loadStatsFromTsv('gdas') : Promise.resolve(null),
    needsVdasFallback ? loadStatsFromTsv('vdas') : Promise.resolve(null),
  ]);

  const gdasRows = apiGdasRows ?? (gdasTsv?.rows as ApiGdasStatsEntry[]);
  const vdasRows = apiVdasRows ?? (vdasTsv?.rows as ApiVdasStatsEntry[]);

  return {
    gdas: apiGdasRows ? entriesToBySource(apiGdasRows) : gdasTsv!.bySource,
    vdas: apiVdasRows ? entriesToBySource(apiVdasRows) : vdasTsv!.bySource,
    gdasRows,
    vdasRows,
  };
}

export function loadLatestSourceStats() {
  if (!cachedPromise) {
    cachedPromise = loadLatestSourceStatsUncached();
  }
  return cachedPromise;
}

export function getSourceStatsColumns(fileKey: SourceStatsFileKey): readonly string[] {
  return fileKey === 'gdas' ? GDAS_STATS_COLUMNS : VDAS_STATS_COLUMNS;
}
